import { SageClassnames, Badge, Frame, Icon } from '@kajabi/sage-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import { localizedPriceFromMinorUnit } from 'apps/commerce/common/checkout/util';
import SubscriptionFrequencyText from './SubscriptionFrequencyText';
import usePriceDisplay from '../hooks/usePriceDisplay';

export const PriceDisplay = ({ offer, displayPrice, isWideLayout, checkoutConfigData }) => {
  const { t } = useTranslation();
  const { getValues } = useFormContext();
  const { price, quantity, quantityLimitDisplayThreshold, quantityRemaining } = getValues();

  const { effectiveDisplayPrice, hasPriceTextOverride } = usePriceDisplay(
    offer,
    displayPrice,
    checkoutConfigData,
  );

  const { title: offerTitle, imageUrl: offerImage } = offer;

  // Subscription-related data
  const subscriptionData = {
    isRecurring: price.priceType === 'recurring',
    isTrialSubscription: price.subscriptionPlanAttributes?.trialPeriodDays > 0,
    trialPeriodDays: price.subscriptionPlanAttributes?.trialPeriodDays,
    paymentType: price.subscriptionPlanAttributes?.paymentType,
    interval: price.subscriptionPlanAttributes?.interval,
    intervalCount: price.subscriptionPlanAttributes?.intervalCount,
    numberOfPayments: price.subscriptionPlanAttributes?.numberOfPayments,
  };

  // Quantity-related data
  const quantityData = {
    showOffersRemaining:
      quantityRemaining > 0 && quantityRemaining <= quantityLimitDisplayThreshold,
    quantityRemaining,
    quantity,
  };

  // Determine if currency badge should be displayed
  const shouldShowCurrencyBadge = () => {
    // Don't show if price text is overridden
    if (hasPriceTextOverride) return false;

    // Don't show if price is 0 and price strategy is fixed
    if (price.amount === 0 && price.priceStrategy === 'fixed') return false;

    // Show in all other cases
    return true;
  };

  // For single items, show the price as provided to the component
  const PriceDisplayAmount = () => (
    <Frame
      direction={Frame.DIRECTIONS.HORIZONTAL}
      align={Frame.ALIGNMENTS.CENTER_RIGHT}
      gap={Frame.GAPS.XS}
    >
      {shouldShowCurrencyBadge() && <Badge isInteractive={false} value={price.currency} />}
      <span
        id="offer-price"
        className={`embedded-checkout-price-display__offer-price ${SageClassnames.TYPE.HEADING_1}`}
      >
        {effectiveDisplayPrice}
      </span>
    </Frame>
  );

  // For quantity purchases, display the total price and unit price
  const QuantityDisplayAmount = () => {
    const totalAmount = localizedPriceFromMinorUnit(
      price.currency,
      price.amount * quantityData.quantity,
      price.exponent,
    );
    const unitAmount = localizedPriceFromMinorUnit(price.currency, price.amount, price.exponent);

    return (
      <>
        <Frame
          direction={Frame.DIRECTIONS.HORIZONTAL}
          align={Frame.ALIGNMENTS.CENTER_RIGHT}
          gap={Frame.GAPS.XS}
        >
          {shouldShowCurrencyBadge() && <Badge isInteractive={false} value={price.currency} />}
          <span
            id="offer-price"
            className={`embedded-checkout-price-display__offer-price ${SageClassnames.TYPE.HEADING_1}`}
          >
            {totalAmount}
          </span>
        </Frame>
        <Frame align={Frame.ALIGNMENTS.CENTER_RIGHT}>
          <span id="unit-price" className={SageClassnames.TYPE_COLORS.GREY_600}>
            {unitAmount}/{t('form.each')}
          </span>
        </Frame>
      </>
    );
  };

  const SubscriptionDetails = () => {
    if (!subscriptionData.isRecurring) return null;

    return (
      <>
        {subscriptionData.isRecurring && subscriptionData.paymentType === 'multipay' && (
          <span className={`${SageClassnames.TYPE.BODY_SMALL}`}>
            {subscriptionData.numberOfPayments}{' '}
            {t(`form.${subscriptionData.interval}`).toLowerCase()} {t('form.payments')}
          </span>
        )}
        {subscriptionData.isRecurring && subscriptionData.paymentType === 'subscription' && (
          <span className={`${SageClassnames.TYPE.BODY_SMALL}`}>
            <SubscriptionFrequencyText
              intervalCount={subscriptionData.intervalCount}
              interval={subscriptionData.interval}
            />
          </span>
        )}
        {subscriptionData.isTrialSubscription && (
          <Badge
            isInteractive={false}
            value={`${subscriptionData.trialPeriodDays} ${t('form.trial_days')}`}
          />
        )}
      </>
    );
  };

  const RemainingOffers = () => {
    if (!quantityData.showOffersRemaining) return null;

    return (
      <span className={`${SageClassnames.TYPE.BODY_SMALL} ${SageClassnames.TYPE_COLORS.RED_600}`}>
        {t('form.items_remaining', { quantity: quantityData.quantityRemaining })}
      </span>
    );
  };

  const OfferImage = () => {
    if (!offerImage) {
      return (
        <div className="embedded-checkout-price-display__icon-container">
          <Icon color={Icon.COLORS.GREY_600} icon={Icon.ICONS.IMAGE} size={Icon.SIZES.XL} />
        </div>
      );
    }

    return (
      <div className="embedded-checkout-price-display__image-container">
        <img
          src={offerImage}
          className="embedded-checkout-price-display__image"
          width="142"
          height="80"
          alt=""
          loading="lazy"
        />
      </div>
    );
  };

  // Determine which price component to display based on quantity
  const PriceTitle = () => {
    if (quantityData.quantity > 1 && !hasPriceTextOverride) {
      return <QuantityDisplayAmount />;
    }

    return <PriceDisplayAmount />;
  };

  // Offer title component
  const OfferTitle = () => <h4 className={`${SageClassnames.TYPE.HEADING_4}`}>{offerTitle}</h4>;

  return (
    <div
      className={`embedded-checkout-price-display ${
        offerImage ? 'embedded-checkout-price-display--image' : ''
      } ${isWideLayout ? 'embedded-checkout-price-display--wide' : ''}`}
    >
      <OfferTitle />
      <Frame
        direction={Frame.DIRECTIONS.HORIZONTAL}
        align={Frame.ALIGNMENTS.CENTER_SPREAD}
        gap={Frame.GAPS.XS}
      >
        <OfferImage />
        <Frame
          direction={Frame.DIRECTIONS.VERTICAL}
          align={Frame.ALIGNMENTS.CENTER_RIGHT}
          gap={Frame.GAPS.XXS}
          className="embedded-checkout-price-display__offer"
        >
          <PriceTitle />
          <SubscriptionDetails />
          <RemainingOffers />
        </Frame>
      </Frame>
    </div>
  );
};

PriceDisplay.defaultProps = {
  isWideLayout: true,
  checkoutConfigData: null,
};

PriceDisplay.propTypes = {
  displayPrice: PropTypes.string.isRequired,
  offer: PropTypes.shape({
    title: PropTypes.string,
    imageUrl: PropTypes.string,
    priceTextOverride: PropTypes.string,
  }).isRequired,
  isWideLayout: PropTypes.bool,
  checkoutConfigData: PropTypes.shape({
    priceTextOverride: PropTypes.string,
    priceTextOverrideEnabled: PropTypes.bool,
  }),
};
